// Per-challenge metadata + theme. Single source of truth for the landing page
// and the individual challenge detail page. The `id` field matches the
// Firestore document ID at gpxMapConfigs/{id} and the URL segment after
// /challenge/. `switchCheckpointIndex` mirrors dossier_config.json — the
// route line is drawn with `primary` before this index and `secondary` after.
//
// Colour palettes were sampled by eye from the 2D style previews under
// /Visual Assets/Maps/ (and generated checkpoint images where 2D previews
// were missing — Caravan Atlas, Imperial Dispatch, Mongol Khanate).

export const CHALLENGE_CATALOG = [
  {
    id: 'japan',
    slug: 'japan',
    title: 'The Nakasendo',
    medal: 'Nakasendo, finisher medal.png',
    subtitle: 'Karuizawa to Kyoto, Japan',
    description:
      'Follow the ancient mountain road through cedar forests, samurai post towns, and mist-covered passes. At every checkpoint, unlock the story and art of Edo-era Japan.',
    styles: ['Japanese (Sumi)', 'Sakuras'],
    switchCheckpointIndex: null,
    featured: true,
    // Hand-curated 4-act narrative breakdown unique to Japan. New challenges
    // omit this and the page derives a 2-act split from `styles` +
    // `switchCheckpointIndex` instead, with no act titles.
    acts: [
      { name: 'The Highland Storm', style: 'Sumi Storm', startCp: 0, endCp: 7 },
      { name: 'The Frozen Valley', style: 'Yukiguni', startCp: 8, endCp: 11 },
      { name: "The Samurai's Path", style: 'Japanese', startCp: 12, endCp: 16 },
      { name: 'The Blossom Capital', style: 'Sakuras', startCp: 17, endCp: 23 },
    ],
    theme: {
      primary: '#c4956a',
      primaryDark: '#b8845a',
      secondary: '#c98b8b',
      tint: '#f5e6e6',
      onPrimary: '#ffffff',
    },
    // Page-scoped background palette. Independent of the app's --bg-*
    // tokens — exposed as --gpx-bg-*/--gpx-border-* so global rebrands of
    // index.css can't regress the challenge pages. Japan keeps the original
    // cream so the Nakasendo looks unchanged.
    surfaces: {
      bgPrimary: '#FAF9F6',
      bgSecondary: '#f5f0e8',
      bgTertiary: '#efe8dc',
      bgCard: '#ffffff',
      borderPrimary: '#e8ddd0',
      borderSecondary: '#d4c4b0',
      bgLocked: '#2a2a2a',         // dark fog over unexplored checkpoints
    },
  },
  {
    id: 'Caravan Road',
    slug: 'caravan-road',
    title: 'The Caravan Road',
    medal: 'Caravan Road, Finisher Medal.png',
    subtitle: 'Skoura to Erfoud, Morocco',
    description:
      'Cross the kasbahs of the Dades Valley, then push east into the Sahara fringe past oasis villages and fossil fields. The art shifts from desert atlas to Timbuktu manuscript.',
    styles: ['Caravan Atlas', 'Timbuktu Manuscripts'],
    switchCheckpointIndex: 6,
    theme: {
      primary: '#c87f3a',
      primaryDark: '#a86326',
      secondary: '#c8552d',
      tint: '#f5e8d4',
      onPrimary: '#fff5e8',
    },
    // Warm Sahara sand (Caravan Atlas) + parchment (Timbuktu Manuscripts).
    surfaces: {
      bgPrimary: '#faf3e3',
      bgSecondary: '#f0e4cc',
      bgTertiary: '#e6d4ad',
      bgCard: '#fffbf2',          // very faint sand tint
      borderPrimary: '#d8c8a4',
      borderSecondary: '#b8a47c',
      bgLocked: '#3a2c1a',        // Sahara night umber
    },
  },
  {
    id: 'Barrier of Spears',
    slug: 'barrier-of-spears',
    title: 'Barrier of Spears',
    medal: 'Barrier of spears, finisher medal.png',
    subtitle: 'Drakensberg to KZN Midlands, Lesotho / South Africa',
    description:
      'Climb the Basotho highlands of Lesotho, then drop into the KwaZulu-Natal foothills. The style transitions from Basotho blanket geometry to Great Zimbabwe stonework.',
    styles: ['Basotho Blankets', 'Great Zimbabwe'],
    switchCheckpointIndex: 8,
    theme: {
      primary: '#1f4a8a',
      primaryDark: '#163866',
      secondary: '#c2a032',
      tint: '#e8edf5',
      onPrimary: '#f0f4ff',
    },
    // Cool stone (Basotho Blankets) + ochre warmth (Great Zimbabwe).
    surfaces: {
      bgPrimary: '#f3eee4',
      bgSecondary: '#e6dccb',
      bgTertiary: '#d8ccb6',
      bgCard: '#fafaf6',           // very faint stone tint
      borderPrimary: '#cabe9c',
      borderSecondary: '#a89b7a',
      bgLocked: '#262a30',         // mountain stone night
    },
  },
  {
    id: 'Dolomites Traverse',
    slug: 'dolomites-traverse',
    title: 'The Dolomites Traverse',
    medal: 'Dolomites Traverse, finisher medal.png',
    subtitle: 'South Tyrol to the Venetian Plain, Northern Italy',
    description:
      'From the alpine spires of South Tyrol down through Cadore and the Piave, finishing on the Venetian plain. Mythic engraving gives way to Italian Renaissance cartography.',
    styles: ['Mythic', 'Italian Renaissance'],
    switchCheckpointIndex: 6,
    theme: {
      primary: '#2a8a8a',
      primaryDark: '#1f6868',
      secondary: '#9a7b4f',
      tint: '#d8ebe7',
      onPrimary: '#f0fcfa',
    },
    // Aged parchment (Mythic) shading into sage cream (Italian Renaissance).
    surfaces: {
      bgPrimary: '#f6f1e3',
      bgSecondary: '#e8e0c8',
      bgTertiary: '#d6cba8',
      bgCard: '#fbfaf2',           // very faint sage tint
      borderPrimary: '#c8be9a',
      borderSecondary: '#a8a07c',
      bgLocked: '#252a23',         // alpine forest ink
    },
  },
  {
    id: 'Annapurna Circuit',
    slug: 'annapurna-circuit',
    title: 'The Annapurna Circuit',
    medal: 'Annapurna Circuit, finisher medal.png',
    subtitle: 'Hindu Marsyangdi to Buddhist Mustang, Nepal',
    description:
      'Trek the Marsyangdi up to Thorong La and down into Mustang. The art shifts from Mughal miniature lowlands to Tibetan thangka high country at the pass.',
    styles: ['Mughal Miniature', 'Tibetan Thangka'],
    switchCheckpointIndex: 10,
    theme: {
      primary: '#2d5f8a',
      primaryDark: '#1d4570',
      secondary: '#c95e2e',
      tint: '#e6ecf2',
      onPrimary: '#f5faff',
    },
    // Cool cream (Mughal Miniature) warming toward saffron (Tibetan Thangka).
    surfaces: {
      bgPrimary: '#f1ede2',
      bgSecondary: '#e3dac4',
      bgTertiary: '#d2c4a4',
      bgCard: '#faf9f3',           // very faint warm cream
      borderPrimary: '#c4b896',
      borderSecondary: '#a4977a',
      bgLocked: '#1d2330',         // deep Himalayan indigo
    },
  },
  {
    id: 'Silk Road Heights',
    slug: 'silk-road-heights',
    title: 'The Silk Road Heights',
    medal: 'Silk Road Heights, Finisher Medal.png',
    subtitle: 'Naxi Lijiang to Tibetan Shangri-La, Yunnan, China',
    description:
      'From the Naxi old town of Lijiang up Tiger Leaping Gorge to the Tibetan plateau at Shangri-La. The style turns from imperial dispatch scroll to Mongol khanate banner.',
    styles: ['Imperial Dispatch', 'Mongol Khanate'],
    switchCheckpointIndex: 3,
    theme: {
      primary: '#9a3b2f',
      primaryDark: '#7a2e25',
      secondary: '#a83533',
      tint: '#f2e3df',
      onPrimary: '#fff0eb',
    },
    // Aged scroll paper (Imperial Dispatch) into rose paper (Mongol Khanate).
    surfaces: {
      bgPrimary: '#f7ece2',
      bgSecondary: '#ecdacb',
      bgTertiary: '#ddbeac',
      bgCard: '#fdf6f0',           // very faint rose-paper tint
      borderPrimary: '#d4baa6',
      borderSecondary: '#b09684',
      bgLocked: '#2e2024',         // silk-road night mauve
    },
  },
  {
    id: 'Continental Divide',
    slug: 'continental-divide',
    title: 'The Continental Divide',
    medal: 'Continental Divide Finisher medal.png',
    subtitle: 'Golden to Grand Lake, Colorado, USA',
    description:
      'Climb the Front Range mining belt, cross the Divide at Rollins Pass, and descend through Winter Park to Grand Lake. Frontier survey gives way to WPA poster.',
    styles: ['Frontier Survey', 'WPA Poster'],
    switchCheckpointIndex: 12,
    theme: {
      primary: '#b87a4a',
      primaryDark: '#94613a',
      secondary: '#d9a83a',
      tint: '#f3e4d2',
      onPrimary: '#fff5e6',
    },
    // Sepia parchment (Frontier Survey) into mustard cream (WPA Poster).
    surfaces: {
      bgPrimary: '#f5ebd8',
      bgSecondary: '#ecdec0',
      bgTertiary: '#ddc99e',
      bgCard: '#fdf8ed',           // very faint parchment tint
      borderPrimary: '#d4c2a0',
      borderSecondary: '#b29e7a',
      bgLocked: '#2e2620',         // frontier dusk sepia
    },
  },
  {
    id: 'Volcanic Spine',
    slug: 'volcanic-spine',
    title: 'The Volcanic Spine',
    medal: 'Volcanic Spine, finisher medal.png',
    subtitle: "K'iche' Highlands to Kaqchikel Crater, Guatemala",
    description:
      'Traverse the volcanic backbone of Guatemala from the K’iche’ highlands to the Kaqchikel crater rim. Aztec codex motifs cede to Andean Incan weaves.',
    styles: ['Aztec Codex', 'Andean Incan'],
    switchCheckpointIndex: 7,
    theme: {
      primary: '#a83a32',
      primaryDark: '#862a23',
      secondary: '#9d2e2e',
      tint: '#f5dfd9',
      onPrimary: '#fff2ec',
    },
    // Warm volcanic cream (Aztec Codex) into clay (Andean Incan).
    surfaces: {
      bgPrimary: '#f3e6d8',
      bgSecondary: '#ead4be',
      bgTertiary: '#d8b896',
      bgCard: '#fdf6ee',           // very faint clay tint
      borderPrimary: '#c8a482',
      borderSecondary: '#a8826a',
      bgLocked: '#2d1f1a',         // volcanic-ash night
    },
  },
];

// Normalised lookup key — case-insensitive, treats '-', '_', and space as equivalent.
// Lets us match catalog entries against Firestore doc ids that might be
// 'Continental Divide', 'continental-divide', or 'continental_divide'.
const normaliseKey = (s) => (s || '').toString().toLowerCase().replace(/[\s_-]+/g, '-');

const _byKey = {};
CHALLENGE_CATALOG.forEach((c) => {
  _byKey[normaliseKey(c.id)] = c;
  if (c.slug) _byKey[normaliseKey(c.slug)] = c;
});

export const lookupChallenge = (id) => _byKey[normaliseKey(id)] || null;

// Finisher medal artwork lives at gs://mythic-maps.firebasestorage.app/assets/finisher_medals/.
// Filenames are case-sensitive and inconsistently punctuated, so each catalog entry
// carries its exact `medal` filename. Accepts a catalog entry or an id/slug.
const FINISHER_MEDAL_DIR = 'assets/finisher_medals/';
export const getFinisherMedalUrl = (entryOrId) => {
  const entry = typeof entryOrId === 'string' ? lookupChallenge(entryOrId) : entryOrId;
  if (!entry?.medal) return null;
  return `https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/${encodeURIComponent(FINISHER_MEDAL_DIR + entry.medal)}?alt=media`;
};

// Back-compat: keep exact-id map for existing call sites.
export const CHALLENGE_BY_ID = CHALLENGE_CATALOG.reduce((acc, c) => {
  acc[c.id] = c;
  return acc;
}, {});

// Exactly one entry should carry `featured: true`. Rotating the featured slot
// is a one-line catalog change. Warn loudly in dev if the invariant breaks.
if (process.env.NODE_ENV !== 'production') {
  const featuredCount = CHALLENGE_CATALOG.filter((c) => c.featured).length;
  if (featuredCount !== 1) {
    // eslint-disable-next-line no-console
    console.warn(
      `[challengeCatalog] Expected exactly one featured challenge, found ${featuredCount}. ` +
      `Set { featured: true } on exactly one CHALLENGE_CATALOG entry.`
    );
  }
}

// The Seven Mountainous Wonders collection: every non-featured entry in the
// catalog. If you add more challenges outside the collection, gate them here.
export const SEVEN_WONDERS_IDS = CHALLENGE_CATALOG
  .filter((c) => !c.featured)
  .map((c) => c.id);

export const DEFAULT_THEME = {
  primary: '#c4956a',
  primaryDark: '#b8845a',
  secondary: '#c98b8b',
  tint: '#f5e6e6',
  onPrimary: '#ffffff',
};

// Fallback surfaces palette used when no catalog entry matches the configId.
// Mirrors Japan's cream so legacy / unknown configIds render cleanly.
export const DEFAULT_SURFACES = {
  bgPrimary: '#FAF9F6',
  bgSecondary: '#f5f0e8',
  bgTertiary: '#efe8dc',
  bgCard: '#ffffff',
  borderPrimary: '#e8ddd0',
  borderSecondary: '#d4c4b0',
  bgLocked: '#2a2a2a',
};
